import React from 'react';
import { SxProps, TextField, TextFieldProps, Theme, Typography } from '@mui/material';
import { Control, Controller, Path, RegisterOptions } from 'react-hook-form';

type NumberType = 'integer' | 'decimal' | 'negativeInteger' | 'negativeDecimal';

// `type="number"` is deliberately avoided: it accepts `e`/`+`/`-`, mutates on scroll-wheel, reports
// an empty string for any invalid input, and ignores maxLength. Instead this is a text field whose
// onChange strips anything that doesn't belong, with inputMode/pattern to get the right iOS keypad.
export function sanitizeNumericInput(raw: string, numberType: NumberType, decimalScale = 2): string {
  const allowNegative = ['negativeInteger', 'negativeDecimal'].includes(numberType);
  const sign = allowNegative && raw.trimStart().startsWith('-') ? '-' : '';
  if (numberType === 'integer' || numberType === 'negativeInteger') {
    // `\D` strips the '-' too, so prepend `sign` back.
    return sign + raw.replace(/\D/g, '');
  }
  // Drop everything but digits and dots, then collapse to a single (left-most) dot.
  const cleaned = raw.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
  const dot = cleaned.indexOf('.');
  const body = dot === -1 || !Number.isFinite(decimalScale) ? cleaned : cleaned.slice(0, dot + 1 + decimalScale);
  return sign + body;
}

const numberTypeToInputMode: Record<NumberType, 'numeric' | 'decimal' | 'text'> = {
  integer: 'numeric',
  negativeInteger: 'text',
  decimal: 'decimal',
  negativeDecimal: 'text',
};
const numberTypeToPattern: Record<NumberType, string> = {
  integer: '[0-9]*',
  negativeInteger: '-?[0-9]*',
  decimal: '[0-9]*[.]?[0-9]*',
  negativeDecimal: '-?[0-9]*[.]?[0-9]*',
};

type NumericFieldBaseProps = {
  // 'integer' -> digits only, 0-9 keypad on iOS. 'decimal' (default) -> one decimal point allowed,
  // decimal keypad on iOS. The 'negative*' variants also permit a single leading '-'; since the iOS
  // numeric/decimal keypads have no minus key they fall back to the full ('text') keyboard.
  numberType?: NumberType;
  // Max digits after the decimal point for the 'decimal' / 'negativeDecimal' types. Default 2
  // (currency). Pass Infinity to allow any length.
  decimalScale?: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  label?: React.ReactNode;
  name?: string;
  // Receives the already-sanitized string. Hold this string in state as-is and parse it (currency(),
  // parseInt()) only when you validate or submit -- keeping it a string is what lets someone type
  // "1." or clear the field without React fighting the caret.
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  value: string;
  inputProps?: TextFieldProps['inputProps'];
};

export const NumericFieldBase = React.forwardRef(function NumericFieldBase(
  {
    numberType = 'decimal',
    decimalScale = 2,
    disabled,
    error,
    helperText,
    label,
    name,
    onChange,
    placeholder,
    size,
    sx,
    value,
    inputProps,
  }: NumericFieldBaseProps,
  _ref: React.Ref<HTMLInputElement>,
) {
  return (
    <TextField
      disabled={disabled}
      error={error}
      helperText={helperText}
      label={label}
      name={name}
      placeholder={placeholder}
      size={size}
      sx={sx}
      type="text"
      value={value}
      onChange={event => onChange(sanitizeNumericInput(event.target.value, numberType, decimalScale))}
      inputProps={{
        inputMode: numberTypeToInputMode[numberType],
        // `pattern` is for iOS before 13.4, where inputMode wasn't honored.
        pattern: numberTypeToPattern[numberType],
        autoComplete: 'off',
        ...inputProps,
      }}
    />
  );
});

type NumericFieldProps<TFieldValues extends { [key: string]: unknown }> = {
  control: Control<TFieldValues>;
  decimalScale?: number;
  disabled?: boolean;
  helperText?: string;
  label?: React.ReactNode;
  name: string;
  numberType?: NumberType;
  placeholder?: string;
  rules?: Omit<RegisterOptions<TFieldValues>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
};

function NumericField<TFieldValues extends { [key: string]: unknown }>({
  control,
  decimalScale,
  disabled,
  helperText,
  label,
  name,
  numberType,
  placeholder,
  rules,
  size,
  sx,
}: NumericFieldProps<TFieldValues>) {
  return (
    <Controller
      control={control}
      name={name as Path<TFieldValues>}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const displayHelperText = error?.message ? (
          <Typography variant="caption" color="error">
            {error.message}
          </Typography>
        ) : (
          helperText && <Typography variant="caption">{helperText}</Typography>
        );

        return (
          <NumericFieldBase
            decimalScale={decimalScale}
            disabled={disabled}
            error={!!error}
            helperText={displayHelperText}
            label={label}
            name={name}
            numberType={numberType}
            placeholder={placeholder}
            size={size}
            sx={sx}
            value={(value as string) ?? ''}
            onChange={onChange}
          />
        );
      }}
    />
  );
}

export default NumericField;
